import React, { useState, useRef, useEffect, useCallback, LegacyRef } from "react";
import ReactPlayer from "react-player";
import "../../styles.css";
import ClassGridView from "./Grid/ClassGridView";
import ClassSpotlightVideo from "./Spotlight/ClassSpotlightView";
import ClassControl from "./ClassControl";
import { VideoContainer, Label } from "./ClassVideo.css";

interface VideoPlayerProps {
  title: string;
  originalLinks: {
    url1: string;
    url2: string;
    url3: string;
    url4: string;
    title: string;
    level: string;
    apparatus: string;
    props: string;
    body: string;
  };
}

function almost(a: number, b: number, delta = 0.01){
  return Math.abs(a - b) < delta
}

function ClassVideo({ originalLinks }: VideoPlayerProps) {
  const [grid, setGrid] = useState(true);
  const [spotlight, setSpotlight] = useState(1);

  const videoPlayerRef1 = useRef<ReactPlayer>(null);
  const videoPlayerRef2 = useRef<ReactPlayer>(null);
  const videoPlayerRef3 = useRef<ReactPlayer>(null);
  const videoPlayerRef4 = useRef<ReactPlayer>(null);

  const [videoState, setVideoState] = useState({
    playing: false,
    muted: false,
    volume: 0.5,
    seeking: false,
    Buffer: true,
    loop: true
  });

  const { 
    title,
    level,
    apparatus,
    props,
    body
  } = originalLinks;

  //Destructuring the properties from the videoState
  const {
    playing,
    muted,
    volume,
    seeking,
    loop
  } = videoState;

  const [currentTime, setCurrentTime] = useState<number>(0);
  const currentTimeAfterRerender = React.useRef<number>(0);
  const updateCurrentTimeAfterRerender = React.useRef<boolean>(false);

  useEffect(() => {
    if (!grid && updateCurrentTimeAfterRerender.current) {
      setCurrentTime(currentTimeAfterRerender.current);
      updateCurrentTimeAfterRerender.current = false;
      synchronizeVideosToCurrentTime(currentTimeAfterRerender.current);
    }
  });

  const muteControl = () => {
    if (videoState.muted) {
      setVideoState({...videoState, muted: false, volume: 0.5});
    } else {
      setVideoState({...videoState, muted: true, volume: 0});
    }
  };

  const [links, setLinks] = useState({
    url1: originalLinks.url1,
    url2: originalLinks.url2,
    url3: originalLinks.url3,
    url4: originalLinks.url4,
  });

  const playPauseHandler = () => {
    setVideoState({ ...videoState, playing: !videoState.playing });
  };

  const fastForwardOnRef = (videoRef: React.RefObject<ReactPlayer>) => {
    videoRef.current?.seekTo(videoRef.current?.getCurrentTime() + 10);
  };

  const rewindOnRef = (videoRef: React.RefObject<ReactPlayer>) => {
    videoRef.current?.seekTo(videoRef.current?.getCurrentTime() - 10);
  };

  const restartOnRef = (videoRef: React.RefObject<ReactPlayer>) => {
    videoRef.current?.seekTo(0);
  };

  const handleSeekChange = (e : any) => {
    videoPlayerRef1.current?.seekTo(e.target.value);
    videoPlayerRef2.current?.seekTo(e.target.value);
    videoPlayerRef3.current?.seekTo(e.target.value);
    videoPlayerRef4.current?.seekTo(e.target.value);
  }

  const updateRefsWithCurrentTime = () => {
    const videoTime = videoPlayerRef1.current?.getCurrentTime();
    if (videoTime && !almost(videoTime, currentTime)){
      videoPlayerRef1.current?.seekTo(currentTime);
      videoPlayerRef2.current?.seekTo(currentTime);
      videoPlayerRef3.current?.seekTo(currentTime);
      videoPlayerRef4.current?.seekTo(currentTime);
    }
  }

  const synchronizeVideosToCurrentTime = (newCurrentTime: number) => {
    videoPlayerRef1.current?.seekTo(newCurrentTime);
    videoPlayerRef2.current?.seekTo(newCurrentTime);
    videoPlayerRef3.current?.seekTo(newCurrentTime);
    videoPlayerRef4.current?.seekTo(newCurrentTime);
    //console.log(`all videos synchronized to ${newCurrentTime}`);
    setCurrentTime(newCurrentTime);
  };

  const handleProgress = (state : any) => {
    if (!seeking) {
      setCurrentTime(state.playedSeconds);
    }
  };

  const handleVolumeChange = (e : any) => {
    setVideoState({...videoState, volume: e.target.value, muted: e.target.value === 0 ? true: false});
  }

  const fastForward = () => {
    if (
      videoPlayerRef1 !== null &&
      videoPlayerRef2 !== null &&
      videoPlayerRef3 !== null &&
      videoPlayerRef4 !== null
    ) {
      fastForwardOnRef(videoPlayerRef1);
      fastForwardOnRef(videoPlayerRef2);
      fastForwardOnRef(videoPlayerRef3);
      fastForwardOnRef(videoPlayerRef4);
    }
  };

  const rewind = () => {
    if (
      videoPlayerRef1 !== null &&
      videoPlayerRef2 !== null &&
      videoPlayerRef3 !== null &&
      videoPlayerRef4 !== null
    ) {
      rewindOnRef(videoPlayerRef1);
      rewindOnRef(videoPlayerRef2);
      rewindOnRef(videoPlayerRef3);
      rewindOnRef(videoPlayerRef4);
    }
  };

  const restart = () => {
    if (
      videoPlayerRef1 !== null &&
      videoPlayerRef2 !== null &&
      videoPlayerRef3 !== null &&
      videoPlayerRef4 !== null
    ) {
      restartOnRef(videoPlayerRef1);
      restartOnRef(videoPlayerRef2);
      restartOnRef(videoPlayerRef3);
      restartOnRef(videoPlayerRef4);
    }
  };

  const switchPlayerToGrid = (
    videoRef: React.RefObject<ReactPlayer>
  ) => {
    if (!grid && videoRef.current) {
      setCurrentTime(videoRef.current.getCurrentTime());
      setGrid(true);
    }
  };

  const switchPlayerToSpotlight = (
    videoNumber: number,
    videoRef: React.RefObject<ReactPlayer>,
    newCurrentTime: number
  ) => {
    if (grid && videoRef.current) {
      setSpotlight(videoNumber);
      setGrid(false);
      currentTimeAfterRerender.current = newCurrentTime;
      updateCurrentTimeAfterRerender.current = true;
    }
  };

  const updateSpotlight = (
    videoNumber: number,
    newCurrentTime: number
  ) => {
    setSpotlight(videoNumber);
    currentTimeAfterRerender.current = newCurrentTime;
    updateCurrentTimeAfterRerender.current = true;
  }

  return (
    <div>
    <VideoContainer grid={grid}>
      { grid ?
        (<ClassGridView
        switchPlayerToSpotlight={switchPlayerToSpotlight}
        playPauseHandler={playPauseHandler}
        playing={playing}
        loop={loop}
        muted={muted}
        ref1={videoPlayerRef1}
        ref2={videoPlayerRef2}
        ref3={videoPlayerRef3}
        ref4={videoPlayerRef4}
        links={links}
        handleProgress={handleProgress}
        updateRefsWithCurrentTime={updateRefsWithCurrentTime}
      />)
      :
      (<ClassSpotlightVideo
        switchPlayerToGrid={switchPlayerToGrid}
        playPauseHandler={playPauseHandler}
        playing={playing}
        loop={loop}
        muted={muted}
        ref1={videoPlayerRef1}
        ref2={videoPlayerRef2}
        ref3={videoPlayerRef3}
        ref4={videoPlayerRef4}
        links={links}
        spotlight={spotlight}
        setSpotlight={setSpotlight}
        handleProgress={handleProgress}
        volume={volume}
        updateRefsWithCurrentTime={updateRefsWithCurrentTime}
        updateSpotlight={updateSpotlight}
      />)
      }
      <ClassControl
        onPlayPause={playPauseHandler}
        playing={playing}
        fastForward={fastForward}
        rewind={rewind}
        restart={restart}
        switchPlayerToGrid={() => switchPlayerToGrid(videoPlayerRef1)}
        muteControl={muteControl}
        muted={muted}
        currentTime={currentTime}
        handleSeekChange={handleSeekChange}
        handleVolumeChange={handleVolumeChange}
        volume={volume}
        videoRef={videoPlayerRef1}
        grid={grid}
      />
      </VideoContainer>
      <Label>
      {title}
      <p>Level: {level} | Apparatus: {apparatus} | Props: {props} | Body: {body}</p>
    </Label>
    </div>
  );
}

export default ClassVideo;
